/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { colors } from "@farmlands/mui-theme";
import {
  Box,
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent as MuiDialogContent,
  DialogProps,
  DialogTitle as MuiDialogTitle,
  Fade,
  Paper,
  PaperProps,
  styled,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import TestIds from "../testIds";
import { shouldForwardProp } from "../util/helpers";
import { error } from "../util/log";

import MaterialSymbol from "./MaterialSymbol";

export const DialogContent = styled(MuiDialogContent, {
  shouldForwardProp: (prop) => shouldForwardProp(["scrollable"], prop),
})<{ scrollable?: boolean }>`
  padding: 24px 32px;
  overflow-x: visible;
  width: 100%;
  overflow-y: ${({ scrollable }) => (scrollable ? "auto" : "hidden")};
`;
// NOTE: use overflow-y: auto instead of 'scroll', otherwise it will reserved the space for scroll even the content is not scrollable

export const StyledPaper = styled(Paper)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  backgroundColor: "white",
  // padding: '8px 16px',
  // gap: '24px',
  borderRadius: "12px",
  width: "100%",
  overflowX: "hidden",

  [theme.breakpoints.down("sm")]: {
    padding: "0",
  },
}));

export const DialogTitle = styled(MuiDialogTitle)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    fontSize: "calc(min(max(17px, 6vw), 24px))",
  },
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
}));

export const HeaderContaier = styled("div")<{ fullscreen?: boolean; divider?: boolean }>(
  ({ fullscreen, divider, theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "24px 32px 16px 32px",
    marginTop: fullscreen ? "var(--app-safe-area-top)" : "",
    borderBottom: divider ? `1px solid ${theme.colors.primary100}` : "",
  })
);

export const BackButton = styled(MuiButton)(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.colors.primary500,
  padding: "0 0 0 0",
  border: "none",
  lineHeight: "150%",
  textDecoration: "none",
  textUnderlineOffset: "2px",

  "&:hover": {
    border: 0,
    background: "inherit",
    color: theme.colors.primary500,
    "& .MuiTypography-root": {
      color: "inherit",
      textDecoration: "underline",
    },
  },
  "&:active": {
    color: theme.colors.primary900,
  },
  "& .MuiTypography-root": {
    color: "inherit",
  },
}));

export const CloseButton = styled(MuiButton)(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.colors.grey700,
  padding: "0 0 0 0",
  border: "none",
  lineHeight: "150%",
  textDecoration: "none",
  textUnderlineOffset: "2px",
  marginLeft: "auto",

  "&:hover": {
    border: 0,
    background: "inherit",
    color: theme.colors.primary500,
    "& .MuiTypography-root": {
      color: "inherit",
      textDecoration: "underline",
    },
  },
  "&:active": {
    color: theme.colors.primary900,
  },
  "& .MuiTypography-root": {
    color: "inherit",
  },
}));

export interface ModalProps extends Omit<DialogProps, "title"> {
  handleClose: () => void;
  testId?: string;
  header?: React.ReactNode;
  customHeader?: React.ReactNode; // this will replace the modal header includes title and close button
  footer?: React.ReactNode;
  scrollRef?: React.Ref<unknown>;
  paperProps?: Partial<PaperProps>;
  scrollable?: boolean;
  title?: string | React.ReactNode;
  headerDivider?: boolean;
  footerDivider?: boolean;
  contentBg?: keyof typeof colors;
  dialogContentSx?: SxProps<Theme>;
  disableBackdropClose?: boolean;
  disableMobileFullScreen?: boolean;
  disableFooterMargin?: boolean;
}

export const Modal = ({
  open,
  title,
  children,
  handleClose,
  testId,
  header,
  customHeader,
  footer,
  scrollRef,
  scrollable = true,
  paperProps,
  headerDivider,
  footerDivider,
  contentBg,
  dialogContentSx,
  disableBackdropClose = false,
  disableMobileFullScreen = false,
  disableFooterMargin = false,
  ...props
}: ModalProps) => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      scroll="paper"
      fullScreen={(isSm && !disableMobileFullScreen) || props.fullScreen}
      PaperComponent={StyledPaper}
      PaperProps={paperProps}
      TransitionComponent={Fade}
      open={open}
      onClose={disableBackdropClose ? undefined : handleClose}
      aria-labelledby={`modal-${testId}`}
      slotProps={{
        backdrop: {
          id: `${testId}--close-backdrop`,
          // eslint-disable-next-line
          //@ts-ignore
          "data-testid": `${testId}--close-backdrop`,
          timeout: 500,
        },
      }}
      data-testid={testId}
      {...props}
    >
      {customHeader ? (
        <HeaderContaier divider={headerDivider} fullscreen={isSm || props.fullScreen}>
          {customHeader}
        </HeaderContaier>
      ) : (
        <HeaderContaier divider={headerDivider} fullscreen={isSm || props.fullScreen}>
          <DialogTitle>
            {title}
            <CloseButton
              id={`${TestIds.components.modal.closeBtn(testId)}--close-button`}
              data-testid={TestIds.components.modal.closeBtn(testId)}
              variant="outlined"
              disableTouchRipple
              onClick={handleClose}
            >
              <Typography variant="body2">Close</Typography>
              <MaterialSymbol>close</MaterialSymbol>
            </CloseButton>
          </DialogTitle>
          {header}
        </HeaderContaier>
      )}
      <DialogContent
        scrollable={scrollable}
        ref={scrollRef}
        sx={{
          mb: 0,
          ml: 0,
          background: contentBg ? theme.colors[contentBg] : "white",
          ...dialogContentSx,
        }}
      >
        {children}
      </DialogContent>
      <Box
        id="dialog-action-portal"
        sx={{ padding: "0px !important", margin: "0px 0px -24px 0px" }}
      />
      <DialogActions
        sx={{
          borderTop: footerDivider ? `1px solid ${colors.primary200}` : "none",
          padding: footer ? "16px 32px" : "0px 32px 24px 32px",
          mt: footer && !disableFooterMargin ? "24px" : "0px",
        }}
      >
        {footer}
      </DialogActions>
    </Dialog>
  );
};

interface ModalActionsProps {
  children: React.ReactNode;
  showDivider?: boolean;
  retryIntervalMs?: number;
  maxRetries?: number;
}

export const ModalActions: React.FC<ModalActionsProps> = ({
  children,
  showDivider = false,
  retryIntervalMs = 100, // Default retry interval of 100ms
  maxRetries = 10, // Default maximum of 10 retries (total 1 second)
}) => {
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);

  useEffect(() => {
    let isMounted = true;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const checkElement = () => {
      if (!isMounted) {
        return;
      }
      const element = document.getElementById("dialog-action-portal");
      if (element) {
        setTargetElement(element);
      } else if (retryCount < maxRetries) {
        setRetryCount((prev) => prev + 1);
        timeoutId = setTimeout(checkElement, retryIntervalMs);
      } else {
        error("The element with id 'dialog-action-portal' was not found after retries.");
      }
    };

    checkElement();

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [retryCount, retryIntervalMs, maxRetries]);

  if (!targetElement) {
    return null; // Optionally, you can render a fallback UI here
  }

  return createPortal(
    <DialogActions
      id="dialog-actions"
      sx={{
        width: "100%",
        borderTop: showDivider ? `1px solid ${colors.primary200}` : 0,
        padding: "16px 32px",
      }}
    >
      {children}
    </DialogActions>,
    targetElement
  );
};

export default Modal;
